import { Navigate, useParams } from 'react-router-dom'
import { AmenityGrid, Band, Btn, SectionHeading, useText } from '@kkf/design'
import { SpecBlock } from '../components/SpecBlock'
import { tourBySlug } from '../data/tours'
import { useT } from '../i18n'

/**
 * One bookable item, in the shape an outfitter's site uses: photograph, the facts at a
 * glance, the long form, what is included, what to bring, what happens in what order, and
 * a way to book.
 *
 * An unknown slug renders the catch-all 404 rather than an empty page — `<Navigate>` to a
 * path no route matches, which is the same trick Partners' member page uses. A tour
 * page with no tour on it is worse than a 404, because it looks like the tour was cancelled.
 *
 * **No banner (2026-09-21).** This was the last page on the platform opening on
 * `Breadcrumb`'s dark photographic band, and for a while it was the argument for
 * `header.plainRoutes` being a list rather than a boolean — every other garage route is
 * pale, and marking the app pale put ink navy on this one's near-black photograph. It is
 * pale now too, so the garage's list covers all eight of its routes. **It stays a list.**
 * The day a page here opens on a banner again the fix is deleting one line, not
 * rediscovering why the nav went white; and `app_website`'s `/` still makes the other
 * claim, so the mechanism has a live example on the platform.
 */
export function TourDetailsPage() {
  const t = useT()
  const s = useText()
  const { slug } = useParams()
  const tour = tourBySlug(slug)
  if (!tour) return <Navigate to="/404" replace />

  return (
    <>
      {/* THE FACTS FIRST — plain. Somebody deciding wants the specs before the prose.
          And the first section of a page with no banner: `.ul-plain-page-top` reserves the
          absolutely-positioned header's height and washes the ground under it. */}
      <Band className="ul-plain-page-top" containerClassName="wow animate__fadeInUp">
        <div className="row row-cols-lg-2 row-cols-1 gy-4 ul-bs-row align-items-start">
          <div className="col">
            <span className="ul-section-sub-title">{tour.kind}</span>
            {/* The page's one `<h1>` — the tour's own name, which `Breadcrumb` printed
                above it until 2026-09-21. */}
            <h1 className="ul-section-title">{s(tour.name)}</h1>
            <p className="ul-section-descr mt-3">{s(tour.tagline)}</p>
            {tour.paragraphs.map((p) => (
              <p className="ul-section-descr mt-3" key={s(p)}>
                {s(p)}
              </p>
            ))}
            <div className="mt-4">
              <Btn to="/contact" icon="fast-forward-double-right-arrows-symbol">
                Enquire To Book
              </Btn>
            </div>
          </div>
          <div className="col">
            <SpecBlock items={tour.specs} pending={tour.pending} />
          </div>
        </div>
      </Band>

      {/* WHAT IS INCLUDED, AND WHAT YOU BRING — linen, side by side */}
      <Band tone="linen" containerClassName="wow animate__fadeInUp">
        <div className="row row-cols-md-2 row-cols-1 gy-4 ul-bs-row">
          <div className="col">
            <SectionHeading subTitle={t('tour.priceEyebrow')} title={t('tour.included')} />
            <AmenityGrid items={tour.includes.map((name) => ({ name: s(name) }))} />
          </div>
          <div className="col">
            <SectionHeading subTitle={t('tour.packEyebrow')} title={t('tour.bring')} />
            <AmenityGrid items={tour.bring.map((name) => ({ name: s(name) }))} />
          </div>
        </div>
      </Band>

      {/* THE ORDER OF THE DAY — ink. Stages, not clock times: nothing here runs to a
          timetable yet, and inventing one would be a commitment nobody has made. */}
      <Band tone="ink" ornament containerClassName="wow animate__fadeInUp">
        <SectionHeading
          className="justify-content-center text-center"
          subTitle={t('tour.howEyebrow')}
          title={t('tour.order')}
        />
        <ol className="ul-spec-list">
          {tour.itinerary.map((step) => (
            <li key={s(step.stage)}>
              <span className="term">{s(step.stage)}</span>
              <span className="detail">{s(step.detail)}</span>
            </li>
          ))}
        </ol>
        <div className="d-flex flex-wrap justify-content-center gap-3 mt-5">
          <Btn to="/contact" icon="fast-forward-double-right-arrows-symbol">
            Enquire To Book
          </Btn>
          <Btn to="/tours" className="ul-btn--2" icon="fast-forward-double-right-arrows-symbol">
            All Tours
          </Btn>
        </div>
      </Band>
    </>
  )
}
